import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { defer, map, Observable, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { AssessmentTaskComment, AssessmentTaskPage } from "./assessment_tasks.types";
import { AssessmentWorkItemEvidenceSummary } from "./assessment_work_items.types";
import { LoginService } from "./login.service";
import { EvidenceRequirementInvalidationService } from "./evidence_requirement_invalidation.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";

export type WorkItemComment = Omit<AssessmentTaskComment, "taskId">;
const id = (value: unknown): number => {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) throw new Error("Invalid comment identity.");
    return value;
};
const revision = (value: unknown): number => {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw new Error("Invalid comment revision.");
    return value;
};
const bool = (value: unknown): boolean => {
    if (typeof value !== "boolean") throw new Error("Invalid comment capability.");
    return value;
};
const timestamp = (value: unknown): string | null => {
    if (value === null) return null;
    if (typeof value === "string" && Number.isFinite(Date.parse(value))) return value;
    throw new Error("Invalid comment timestamp.");
};

@Injectable({ providedIn: "root" })
export class EvidenceCommentsService {
    constructor(private readonly http: HttpClient, private readonly login: LoginService,
        private readonly invalidations: EvidenceRequirementInvalidationService) {}

    list(evidence: AssessmentWorkItemEvidenceSummary, page: number): Observable<AssessmentTaskPage<WorkItemComment>> {
        return this.request(evidence, "GET", undefined, page).pipe(map(data => {
            if (data.page !== page || data.pageSize !== 5 || !Number.isSafeInteger(data.total) || data.total < 0 ||
                !Array.isArray(data.items) || data.items.length > 5) throw new Error("Invalid comment page.");
            return { page, pageSize: 5, total: data.total, items: data.items.map((comment: any) => {
                if (comment.evidenceId !== evidence.id || comment.productLicenseId !== evidence.productLicenseId ||
                    typeof comment.body !== "string" || comment.body.length > 20000) throw new Error("Invalid comment parent.");
                if (comment.author !== null && typeof comment.author?.displayName !== "string") throw new Error("Invalid comment author.");
                return { id: id(comment.id), body: comment.body, revision: revision(comment.revision),
                    author: comment.author === null ? null : { userId: id(comment.author.userId), displayName: comment.author.displayName },
                    createdAt: timestamp(comment.createdAt), updatedAt: timestamp(comment.updatedAt),
                    capabilities: { canEditOwnComment: bool(comment.capabilities?.canEditOwnComment),
                        canDeleteOwnComment: bool(comment.capabilities?.canDeleteOwnComment) } };
            }) };
        }));
    }

    mutate(evidence: AssessmentWorkItemEvidenceSummary, operation: "POST" | "PATCH" | "DELETE",
        body: object, commentId?: number): Observable<unknown> {
        return defer(() => {
            const publish = this.invalidations.capturePublisher();
            return this.request(evidence, operation, body, undefined, commentId).pipe(tap(data => {
                id(data.id); revision(data.revision);
                if (data.evidenceId !== evidence.id || data.productLicenseId !== evidence.productLicenseId ||
                    commentId !== undefined && data.id !== commentId || typeof data.changed !== "boolean" ||
                    operation === "DELETE" && data.deleted !== true) throw new Error("Invalid comment receipt.");
                if (data.changed) publish({ kind: "comment", productLicenseId: evidence.productLicenseId,
                    dueDateMayHaveChanged: false, unfinishedCountMayHaveChanged: false,
                    items: [{ id: evidence.id, before: null, after: null }] });
            }));
        });
    }

    private request(evidence: AssessmentWorkItemEvidenceSummary, method: string, body?: object,
        page?: number, commentId?: number): Observable<any> {
        return defer(() => {
            const session = assessmentTaskSessionIdentity(this.login.getUserSnapshot()), token = this.login.getToken();
            if (!session || !token) throw new Error("Comment session unavailable.");
            const url = `${environment.baseurl.replace(/\/$/, "")}/assessment-work-items/assessments/${id(evidence.productLicenseId)}/evidence/${id(evidence.id)}/comments` +
                (commentId === undefined ? "" : `/${id(commentId)}`) + (page === undefined ? "" : `?page=${id(page)}&pageSize=5`);
            return this.http.request<any>(method, url, { body }).pipe(map(response => {
                if (token !== this.login.getToken() || session !== assessmentTaskSessionIdentity(this.login.getUserSnapshot()) ||
                    response?.success !== true || !response.data || typeof response.data !== "object") throw new Error("Comment context changed.");
                return response.data;
            }));
        });
    }
}
